// src/pages/admin/Analytics.js
import React, { useState } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Download as DownloadIcon,
  CalendarMonth as CalendarIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Star as StarIcon,
  CleaningServices as CleaningIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Notifications as NotificationsIcon,
} from "@mui/icons-material";

// Mock data for analytics
const analyticsData = {
  overview: {
    totalRevenue: 91200,
    revenueGrowth: 15.8,
    totalJobs: 728,
    jobsGrowth: 12.4,
    activeCustomers: 245,
    customersGrowth: 8.7,
    customerSatisfaction: 4.8,
    satisfactionGrowth: 2.1,
  },
  performance: {
    completionRate: 92,
    onTimeRate: 88,
    repeatBusiness: 65,
    customerRetention: 78,
    cleanerRetention: 85,
    responseTime: 15, // minutes
  },
  topMetrics: [
    { label: 'Most Popular Service', value: 'Standard Cleaning', change: '+8%' },
    { label: 'Highest Revenue Area', value: 'Manhattan', change: '+12%' },
    { label: 'Top Performing Cleaner', value: 'Maria Garcia', change: '+15%' },
    { label: 'Best Customer Rating', value: '4.9/5', change: '+0.2' },
  ],
  customerInsights: [
    { metric: 'New Customers', current: 45, previous: 38, change: '+18%' },
    { metric: 'Repeat Customers', current: 156, previous: 142, change: '+10%' },
    { metric: 'Customer Lifetime Value', current: 1250, previous: 1180, change: '+6%' },
    { metric: 'Referral Rate', current: 23, previous: 19, change: '+21%' },
  ],
  topCleaners: [
    { name: 'Maria Garcia', jobs: 156, rating: 4.9, earnings: 12540, efficiency: 95 },
    { name: 'James Wilson', jobs: 132, rating: 4.8, earnings: 9840, efficiency: 92 },
    { name: 'Carlos Rodriguez', jobs: 98, rating: 4.7, earnings: 7560, efficiency: 89 },
    { name: 'Lisa Chen', jobs: 87, rating: 4.8, earnings: 6540, efficiency: 91 },
  ],
  servicePerformance: [
    { service: 'Standard Cleaning', revenue: 41040, jobs: 328, avgRating: 4.7 },
    { service: 'Deep Cleaning', revenue: 22800, jobs: 120, avgRating: 4.8 },
    { service: 'Office Cleaning', revenue: 13680, jobs: 92, avgRating: 4.6 },
    { service: 'Move-In/Out', revenue: 9120, jobs: 38, avgRating: 4.9 },
    { service: 'Specialty', revenue: 4560, jobs: 24, avgRating: 4.5 },
  ]
};

const MetricCard = ({ label, value, change, icon, color }) => {
  const theme = useTheme();
  const isPositive = !change.startsWith('-');

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" color={`${color}.main`} gutterBottom>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {label}
            </Typography>
          </Box>
          <Box sx={{ 
            color: `${color}.main`,
            backgroundColor: alpha(theme.palette[color].main, 0.1),
            borderRadius: '50%',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {icon}
          </Box>
        </Box>
        <Chip 
          label={change} 
          size="small" 
          color={isPositive ? 'success' : 'error'}
          variant="outlined"
        />
      </CardContent>
    </Card>
  );
};

const PerformanceMetric = ({ label, value, max = 100, color }) => {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" fontWeight="600">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight="600" color={color}>
          {value}%
        </Typography>
      </Box>
      <LinearProgress 
        variant="determinate" 
        value={value} 
        sx={{ 
          height: 8, 
          borderRadius: 4,
          backgroundColor: alpha(color, 0.2),
          '& .MuiLinearProgress-bar': {
            backgroundColor: color,
            borderRadius: 4,
          }
        }}
      />
    </Box>
  );
};

export default function Analytics() {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState('monthly');
  const { overview, performance, topMetrics, customerInsights, topCleaners, servicePerformance } = analyticsData;

  const overviewStats = [
    {
      label: "Total Revenue",
      value: `$${overview.totalRevenue.toLocaleString()}`,
      change: `+${overview.revenueGrowth}%`,
      color: "success",
      icon: <MoneyIcon />
    },
    {
      label: "Total Jobs",
      value: overview.totalJobs.toLocaleString(),
      change: `+${overview.jobsGrowth}%`,
      color: "primary",
      icon: <CleaningIcon />
    },
    {
      label: "Active Customers",
      value: overview.activeCustomers.toLocaleString(),
      change: `+${overview.customersGrowth}%`,
      color: "info",
      icon: <PeopleIcon />
    },
    {
      label: "Customer Satisfaction",
      value: `${overview.customerSatisfaction}/5`,
      change: `+${overview.satisfactionGrowth}%`,
      color: "warning",
      icon: <StarIcon />
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Business Analytics
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
            >
              Export Data
            </Button>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Comprehensive business insights and performance metrics
        </Typography>
      </Box>

      {/* Overview Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {overviewStats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <MetricCard {...stat} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Performance Metrics */}
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Performance Metrics
              </Typography>
              <PerformanceMetric 
                label="Job Completion Rate" 
                value={performance.completionRate} 
                color={theme.palette.success.main}
              />
              <PerformanceMetric 
                label="On-Time Rate" 
                value={performance.onTimeRate} 
                color={theme.palette.info.main}
              />
              <PerformanceMetric 
                label="Repeat Business Rate" 
                value={performance.repeatBusiness} 
                color={theme.palette.primary.main}
              />
              <PerformanceMetric 
                label="Customer Retention" 
                value={performance.customerRetention} 
                color={theme.palette.warning.main}
              />
              <PerformanceMetric 
                label="Cleaner Retention" 
                value={performance.cleanerRetention} 
                color={theme.palette.success.main}
              />
            </CardContent>
          </Card>

          {/* Top Metrics */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Key Performance Indicators
              </Typography>
              <List>
                {topMetrics.map((metric, index) => (
                  <ListItem key={index} divider={index < topMetrics.length - 1}>
                    <ListItemText
                      primary={metric.label}
                      secondary={metric.value}
                    />
                    <Chip 
                      label={metric.change} 
                      size="small" 
                      color="success"
                      variant="outlined"
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Customer Insights */}
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Customer Insights
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Metric</TableCell>
                      <TableCell align="right">Current</TableCell>
                      <TableCell align="right">Previous</TableCell>
                      <TableCell align="right">Change</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customerInsights.map((insight, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">
                            {insight.metric}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="600">
                            {typeof insight.current === 'number' && insight.current > 1000 
                              ? `$${insight.current.toLocaleString()}` 
                              : insight.current}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">
                            {typeof insight.previous === 'number' && insight.previous > 1000 
                              ? `$${insight.previous.toLocaleString()}` 
                              : insight.previous}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={insight.change} 
                            size="small" 
                            color={insight.change.startsWith('+') ? 'success' : 'error'}
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Service Performance */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Service Performance
              </Typography>
              <List dense>
                {servicePerformance.map((service, index) => (
                  <ListItem key={index} divider={index < servicePerformance.length - 1}>
                    <ListItemIcon>
                      <CleaningIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={service.service}
                      secondary={`${service.jobs} jobs • Rating: ${service.avgRating}/5`}
                    />
                    <Typography variant="body2" fontWeight="600" color="primary">
                      ${service.revenue.toLocaleString()}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Cleaners */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            Top Performing Cleaners
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Cleaner</TableCell>
                  <TableCell align="right">Jobs Completed</TableCell>
                  <TableCell align="right">Rating</TableCell>
                  <TableCell align="right">Earnings</TableCell>
                  <TableCell align="right">Efficiency</TableCell>
                  <TableCell align="center">Performance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topCleaners.map((cleaner, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                          {cleaner.name.charAt(0)}
                        </Avatar>
                        <Typography variant="subtitle1" fontWeight="600">
                          {cleaner.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight="600">
                        {cleaner.jobs}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                        <StarIcon sx={{ color: theme.palette.warning.main, fontSize: 18 }} />
                        <Typography variant="body1" fontWeight="600">
                          {cleaner.rating}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight="600" color="primary">
                        ${cleaner.earnings.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight="600">
                        {cleaner.efficiency}%
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <LinearProgress 
                        variant="determinate" 
                        value={cleaner.efficiency} 
                        color={cleaner.efficiency > 90 ? "success" : cleaner.efficiency > 80 ? "warning" : "error"}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Quick Insights */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Growth Trends
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Monthly Revenue Growth</Typography>
                  <Typography variant="body2" fontWeight="600" color="success.main">
                    +{overview.revenueGrowth}%
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Customer Acquisition</Typography>
                  <Typography variant="body2" fontWeight="600" color="success.main">
                    +{overview.customersGrowth}%
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Service Demand</Typography>
                  <Typography variant="body2" fontWeight="600" color="success.main">
                    +{overview.jobsGrowth}%
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                <LocationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Geographic Insights
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Most Active Area</Typography>
                  <Typography variant="body2" fontWeight="600">Manhattan</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Fastest Growing</Typography>
                  <Typography variant="body2" fontWeight="600" color="success.main">Brooklyn</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Service Coverage</Typography>
                  <Typography variant="body2" fontWeight="600">5 Areas</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Operational Insights
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Avg. Response Time</Typography>
                  <Typography variant="body2" fontWeight="600">{performance.responseTime} min</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Peak Booking Hours</Typography>
                  <Typography variant="body2" fontWeight="600">9AM - 12PM</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Most Popular Day</Typography>
                  <Typography variant="body2" fontWeight="600">Saturday</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}